import React, { useEffect } from 'react';
import { Phone, Building2, Sun, Trees, Recycle, DraftingCompass, Leaf } from 'lucide-react';
import './ArchitectureSustainability.css';

const ArchitectureSustainability = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fadeIn');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animated').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="arch-sus-page">
      {/* Hero Section */}
      <section className="arch-sus-hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="container">
            <h1 className="display-large animated">Architecture & Sustainability</h1>
            <p className="body-large animated">
              Sustainable architectural design and green energy solutions for a better tomorrow
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="arch-sus-services-section">
        <div className="container">
          <div className="section-header animated">
            <h2 className="heading-1">Our Sustainable Services</h2>
            <p className="body-large">
              Environmentally responsible design and renewable energy solutions tailored for rural and remote communities
            </p>
          </div>

          <div className="arch-sus-grid">
            <div className="arch-sus-card animated">
              <div className="service-icon-large">
                <Building2 size={40} />
              </div>
              <h2 className="heading-2">Sustainable Architecture Design</h2>
              <ul className="arch-sus-list">
                <li>Passive solar building design for energy efficiency</li>
                <li>Eco-friendly building material selection</li>
                <li>Natural ventilation and daylight optimization</li>
                <li>Climate-responsive architectural planning</li>
                <li>Low-impact development strategies</li>
              </ul>
            </div>

            <div className="arch-sus-card animated">
              <div className="service-icon-large">
                <Sun size={40} />
              </div>
              <h2 className="heading-2">Solar Energy Systems</h2>
              <ul className="arch-sus-list">
                <li>Residential and commercial solar installations</li>
                <li>Solar water heating solutions</li>
                <li>Off-grid photovoltaic system design</li>
                <li>Battery storage and energy management</li>
                <li>Solar farm feasibility studies</li>
              </ul>
            </div>

            <div className="arch-sus-card animated">
              <div className="service-icon-large">
                <Recycle size={40} />
              </div>
              <h2 className="heading-2">Green Building Consulting</h2>
              <ul className="arch-sus-list">
                <li>Sustainable building certification guidance</li>
                <li>Energy efficiency audits and recommendations</li>
                <li>Water conservation and rainwater harvesting</li>
                <li>Waste reduction and recycling systems</li>
                <li>Carbon footprint assessment</li>
              </ul>
            </div>

            <div className="arch-sus-card animated">
              <div className="service-icon-large">
                <Trees size={40} />
              </div>
              <h2 className="heading-2">Eco-Community Planning</h2>
              <ul className="arch-sus-list">
                <li>Sustainable village and settlement planning</li>
                <li>Community solar micro-grids</li>
                <li>Shared renewable energy infrastructure</li>
                <li>Green public space design</li>
                <li>Sustainable transport integration</li>
              </ul>
            </div>

            <div className="arch-sus-card animated">
              <div className="service-icon-large">
                <DraftingCompass size={40} />
              </div>
              <h2 className="heading-2">Architectural Drafting & Design</h2>
              <ul className="arch-sus-list">
                <li>2D and 3D architectural drawings</li>
                <li>Site analysis and feasibility studies</li>
                <li>Building plans and permit documentation</li>
                <li>Sustainable material specifications</li>
                <li>Renovation and retrofitting designs</li>
              </ul>
            </div>

            <div className="arch-sus-card animated">
              <div className="service-icon-large">
                <Leaf size={40} />
              </div>
              <h2 className="heading-2">Renewable Energy Consulting</h2>
              <ul className="arch-sus-list">
                <li>Wind and solar resource assessment</li>
                <li>Hybrid renewable energy system design</li>
                <li>Energy storage solutions</li>
                <li>Grid-tie and off-grid system planning</li>
                <li>Energy cost reduction strategies</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Sustainability Matters */}
      <section className="why-sus-section">
        <div className="container">
          <h2 className="heading-1 animated">Why Sustainable Architecture?</h2>
          <div className="why-sus-grid">
            <div className="why-sus-card animated">
              <h3 className="heading-3">Lower Operating Costs</h3>
              <p className="body-medium">
                Energy-efficient buildings significantly reduce electricity and water bills, making them more affordable to maintain over time.
              </p>
            </div>
            <div className="why-sus-card animated">
              <h3 className="heading-3">Environmental Stewardship</h3>
              <p className="body-medium">
                Reduce carbon footprint and environmental impact through responsible design, renewable energy, and sustainable materials.
              </p>
            </div>
            <div className="why-sus-card animated">
              <h3 className="heading-3">Energy Independence</h3>
              <p className="body-medium">
                Solar-powered and passive design buildings remain functional during load-shedding, ensuring uninterrupted operations.
              </p>
            </div>
            <div className="why-sus-card animated">
              <h3 className="heading-3">Healthier Spaces</h3>
              <p className="body-medium">
                Natural ventilation, daylight optimization, and non-toxic materials create healthier indoor environments for occupants.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="arch-sus-cta-section">
        <div className="container">
          <div className="cta-box animated">
            <h2 className="heading-1">Build a Sustainable Future</h2>
            <p className="body-large">Contact us for a consultation on sustainable architecture and energy solutions</p>
            <div className="cta-contact-info">
              <a href="mailto:offgridsolution@gmail.com" className="contact-link">
                <span className="contact-label">Email:</span> offgridsolution@gmail.com
              </a>
              <a href="https://wa.me/27724603624" target="_blank" rel="noopener noreferrer" className="contact-link whatsapp-link">
                <Phone size={20} />
                <span className="contact-label">WhatsApp:</span> +27 72 460 3624
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArchitectureSustainability;
